import superChat from 'ai/reducers/superChat';
import sdnApi from 'ai/services/sdn';
import AddToBasketActions from 'actions/AddToBasketActions';
import Actions from 'actions/Actions';
import LoveActions from 'actions/LoveActions';
import Authentication from 'utils/Authentication';
import { STD_BASKET_TYPE, PRODUCT_RECOMMENDATION_ANALYTICS_CONTEXT } from 'ai/constants/superchat';
import { HEADER_VALUE } from 'constants/authentication';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import {
    MAX_FLYOUT_RECENT_ITEMS, MAX_ONLY_A_FEW_LEFT_IN_LOVES_LIST_FLYOUT, MIN_ITEMS_PER_PAGE, LOVES_LIST
} from 'constants/sharableList';
import { getSuperChatReferrer } from 'ai/utils/sessionStorage';
import { buildBasketAnalyticsData } from 'ai/analytics';
import { isComplementaryEnabled } from 'ai/selectors/killSwitches/isComplementaryEnabledSelector';
import genAISocketService from 'ai/services/socket/socketService';
import store from 'store/Store';

const { feedback } = sdnApi;

const { ACTION_TYPES } = superChat;

const superChatActions = {
    showSuperChat: (showSuperChat, prompts, data) => {
        return {
            type: ACTION_TYPES.SHOW_SUPER_CHAT,
            payload: {
                showSuperChat,
                prompts,
                entryPointData: {
                    ...data,
                    date: new Date()
                }
            }
        };
    },
    superChatConfig: config => {
        return {
            type: ACTION_TYPES.SUPER_CHAT_CONFIG,
            payload: config
        };
    },
    setRequestInFlight: isRequestInFlight => {
        return {
            type: ACTION_TYPES.SET_REQUEST_IN_FLIGHT,
            payload: isRequestInFlight
        };
    },
    submitFeedback: params => {
        return async () => {
            const {
                feedback: feedbackItems, sessionId, lastMessageId, clientId, anonymousId
            } = params || {};

            return feedback({
                feedback: feedbackItems,
                sessionId,
                lastMessageId,
                clientId,
                anonymousId
            });
        };
    },
    addToBasket: (productData, options = {}) => {
        return dispatch => {
            const { isPurchaseHistory = false } = options;
            const { skuList } = productData;
            const firstSku = skuList[0];

            const sku = {
                skuId: firstSku.skuId,
                productId: firstSku.productId
            };

            const basketType = STD_BASKET_TYPE;
            const qty = firstSku.qty || 1;
            const analyticsContext = PRODUCT_RECOMMENDATION_ANALYTICS_CONTEXT;

            const isSuperChatOpen = store.getState()?.superChat?.showSuperChat;
            const sotV425 = isSuperChatOpen ? getSuperChatReferrer() : null;

            const analyticsData = buildBasketAnalyticsData({ ...firstSku, skuId: sku.skuId }, { sotV425, isPurchaseHistory });

            return dispatch(
                AddToBasketActions.addToBasket(
                    sku,
                    basketType,
                    qty,
                    null,
                    analyticsContext,
                    false, // samplePanel
                    analyticsData,
                    false, // showBasketQuickAdd
                    false, // isAutoReplenish
                    '', // replenishmentFrequency
                    firstSku.productId
                )
            );
        };
    },
    addRewardToBasket: (skuId, productId) => {
        return async dispatch => {
            const biApi = (await import('services/api/beautyInsider')).default;

            const data = await biApi.addBiRewardsToCart(skuId, productId);

            if (data?.basket) {
                dispatch(AddToBasketActions.refreshBasket());
            }

            return data;
        };
    },
    updateReduxLoveList: () => {
        return async dispatch => {
            const lovesFromIndexedDB = await Storage.db.getItem(LOCAL_STORAGE.LOVED_ITEMS_SKU_ONLY, false, false, true);
            const loves = Array.isArray(lovesFromIndexedDB.data.skus) ? lovesFromIndexedDB.data.skus : [];
            const totalLovesListItemsCount = lovesFromIndexedDB.data.totalSkus ?? loves.length;

            const loveIds = LoveActions.setShoppingListIds(loves);
            const skuProductIds = LoveActions.setLoveListSkuProductIds(loves);

            dispatch(LoveActions.updateShoppingListIds(loveIds));
            dispatch(LoveActions.updateLoveListSkuProductSet(skuProductIds));
            dispatch(
                LoveActions.updateLovesListReduxOnly({
                    loves,
                    totalLovesListItemsCount,
                    shareLink: lovesFromIndexedDB.data.shareLink
                })
            );
        };
    },
    addToLovesList: product => {
        return (dispatch, getState) => {
            const analyticsData = {
                context: PRODUCT_RECOMMENDATION_ANALYTICS_CONTEXT,
                nextPageContext: PRODUCT_RECOMMENDATION_ANALYTICS_CONTEXT
            };

            // Check if user is logged in, if not show login modal
            return Authentication.requireAuthentication(null, null, analyticsData, null, false, HEADER_VALUE.USER_CLICK)
                .then(() => {
                    // User is authenticated, proceed with adding/removing love directly
                    const { loves } = getState();
                    const skuId = product.default_sku;
                    const productId = product.id;

                    const isCurrentlyLoved = loves.shoppingListIds?.includes(skuId);

                    if (isCurrentlyLoved) {
                        const productLoveData = {
                            skuId,
                            productId: productId,
                            type: 'loves',
                            loveSource: PRODUCT_RECOMMENDATION_ANALYTICS_CONTEXT
                        };

                        return dispatch(
                            LoveActions.removeItemFromSharableList(
                                productLoveData,
                                () => {
                                    const action = LoveActions.modifyLovedItem({
                                        key: LOCAL_STORAGE.LOVED_ITEMS_SKU_ONLY,
                                        skuId,
                                        productId,
                                        listName: LOVES_LIST,
                                        action: 'remove',
                                        callback: superChatActions.updateReduxLoveList
                                    });
                                    dispatch(action);

                                    dispatch(
                                        LoveActions.getFlatLoveListSkusWithDetails({
                                            force: true,
                                            options: {
                                                itemsPerPage: MAX_FLYOUT_RECENT_ITEMS,
                                                currentPage: 1
                                            }
                                        })
                                    );
                                    dispatch(
                                        LoveActions.getLimitedLoveListItems({
                                            force: true,
                                            options: {
                                                itemsPerPage: MAX_ONLY_A_FEW_LEFT_IN_LOVES_LIST_FLYOUT
                                            }
                                        })
                                    );
                                    dispatch(
                                        LoveActions.getAllLists({
                                            force: true,
                                            options: { itemsPerPage: MIN_ITEMS_PER_PAGE },
                                            callback: async () => {
                                                await Storage.db.removeItem(LOCAL_STORAGE.ALL_LOVE_LIST_SKU_ONLY);
                                            }
                                        })
                                    );
                                },
                                error => {
                                    Sephora.logger.error('Failed to remove product from loves:', error);
                                }
                            )
                        );
                    } else {
                        const skuLoveData = {
                            skuId,
                            productId: productId,
                            source: 'productPage',
                            loveSource: PRODUCT_RECOMMENDATION_ANALYTICS_CONTEXT
                        };

                        return dispatch(
                            LoveActions.addItemToSharableList(
                                skuLoveData,
                                () => {
                                    const action = LoveActions.modifyLovedItem({
                                        key: LOCAL_STORAGE.LOVED_ITEMS_SKU_ONLY,
                                        skuId,
                                        productId,
                                        listName: LOVES_LIST,
                                        action: 'add',
                                        callback: superChatActions.updateReduxLoveList
                                    });
                                    dispatch(action);

                                    dispatch(
                                        LoveActions.getFlatLoveListSkusWithDetails({
                                            force: true,
                                            options: {
                                                itemsPerPage: MAX_FLYOUT_RECENT_ITEMS,
                                                currentPage: 1
                                            }
                                        })
                                    );
                                    dispatch(
                                        LoveActions.getLimitedLoveListItems({
                                            force: true,
                                            options: {
                                                itemsPerPage: MAX_ONLY_A_FEW_LEFT_IN_LOVES_LIST_FLYOUT
                                            }
                                        })
                                    );
                                    dispatch(
                                        LoveActions.getAllLists({
                                            force: true,
                                            options: { itemsPerPage: MIN_ITEMS_PER_PAGE },
                                            callback: async () => {
                                                await Storage.db.removeItem(LOCAL_STORAGE.ALL_LOVE_LIST_SKU_ONLY);
                                            }
                                        })
                                    );
                                },
                                error => {
                                    Sephora.logger.error('Failed to add product to loves:', error);
                                }
                            )
                        );
                    }
                })
                .catch(() => {
                    // User cancelled login or authentication failed
                    Sephora.logger.error('User cancelled login or authentication failed for love action');
                });
        };
    },
    closeSuperChat: () => {
        return {
            type: ACTION_TYPES.CLOSE_SUPER_CHAT
        };
    },
    setContextPrompts: prompts => {
        return {
            type: ACTION_TYPES.SET_CONTEXT_PROMPTS,
            payload: prompts
        };
    },
    setWelcomeMatShown: shown => {
        return {
            type: ACTION_TYPES.SET_WELCOME_MAT_SHOWN,
            payload: shown
        };
    },
    setComplementaryCarousel: (sourceProductId, header, products) => {
        return {
            type: ACTION_TYPES.SET_COMPLEMENTARY_CAROUSEL,
            payload: { sourceProductId, header, products }
        };
    },
    clearComplementaryCarousels: () => {
        return {
            type: ACTION_TYPES.CLEAR_COMPLEMENTARY_CAROUSELS
        };
    },
    setChatPaused: isPaused => {
        return {
            type: ACTION_TYPES.SET_CHAT_PAUSED,
            payload: isPaused
        };
    },
    handleAddToBasketFailure: ({ title, message, buttonText } = {}) => {
        return dispatch => {
            dispatch(superChatActions.setChatPaused(true));

            dispatch(
                Actions.showInfoModal({
                    isOpen: true,
                    title,
                    message,
                    buttonText,
                    buttonWidth: '50%',
                    footerDisplay: 'flex',
                    footerJustifyContent: 'center',
                    callback: () => {
                        dispatch(superChatActions.setChatPaused(false));
                    },
                    cancelCallback: () => {
                        dispatch(superChatActions.setChatPaused(false));
                    }
                })
            );
        };
    },
    requestComplementaryProducts: (productId, skuId) => () => {
        if (!productId || !isComplementaryEnabled()) {
            return;
        }

        genAISocketService.sendComplementaryProductsRequest(productId, skuId);
    }
};

export default superChatActions;
